import { FormField } from '@/components/FormField';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { OptionSelect } from '@/components/OptionSelect';
import { inputValueOf, type SchemaProp } from './runBuildModel';

export function SwitchRow({ id, label, description, checked, onChange }: { id: string; label: string; description?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex min-h-11 items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
      <div className="min-w-0">
        <Label htmlFor={id} className="cursor-pointer text-[13px] font-medium">
          {label}
        </Label>
        {description ? <p className="text-xs text-subtle">{description}</p> : null}
      </div>
      <Switch id={id} data-testid={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

interface CustomInputProps {
  name: string;
  prop: SchemaProp;
  value: unknown;
  onChange: (v: unknown) => void;
  hint?: string;
}

/** Generic control for a launch-form property (JSON Schema draft 2020-12): enum → select, boolean → switch, number/string → input. */
export function CustomInput({ name, prop, value, onChange, hint }: CustomInputProps) {
  const id = `run-build-input-${name}`;
  const label = prop.title ?? name;
  if (prop.type === 'boolean') return <SwitchRow id={id} label={label} description={prop.description ?? hint} checked={value === true} onChange={onChange} />;
  const fieldHint = hint ?? prop.description;
  return (
    <FormField id={id} label={label} hint={fieldHint}>
      {Array.isArray(prop.enum) ? <EnumControl id={id} options={prop.enum} value={value} onChange={onChange} /> : <ValueControl id={id} numeric={prop.type === 'number' || prop.type === 'integer'} value={value} onChange={onChange} />}
    </FormField>
  );
}

function EnumControl({ id, options, value, onChange }: { id: string; options: unknown[]; value: unknown; onChange: (v: unknown) => void }) {
  return (
    <OptionSelect
      id={id}
      data-testid={id}
      value={typeof value === 'string' ? value : ''}
      onValueChange={onChange}
      placeholder="Pick"
      options={options.map((o) => ({ value: String(o), label: String(o) }))}
      className="font-mono sm:text-[13px]"
      contentClassName="font-mono"
    />
  );
}

function ValueControl({ id, numeric, value, onChange }: { id: string; numeric: boolean; value: unknown; onChange: (v: unknown) => void }) {
  return (
    <Input
      data-testid={id}
      type={numeric ? 'number' : 'text'}
      value={value === undefined || value === null ? '' : String(value)}
      onChange={(e) => onChange(inputValueOf(e.target.value, numeric))}
      className="font-mono sm:text-[13px]"
    />
  );
}
